import { mockData } from '../mockData';

// Simulate network delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Mock API class
class MockApi {
  private data = mockData;
  


  async get<T>(endpoint: string): Promise<T> {
    await delay(300);
    
    // Handle dynamic routes
    if (endpoint.startsWith('/api/bookings/') && endpoint !== '/api/bookings' && !endpoint.includes('/api/bookings/stats') && !endpoint.includes('/api/bookings/config') && !endpoint.includes('/api/bookings/export')) {
      const bookingId = endpoint.split('/').pop();
      const booking = this.data.bookings?.find((b: any) => b.id === parseInt(bookingId || '0'));
      
      if (endpoint.includes('/customer')) {
        // Return customer details
        return (booking?.customer || null) as T;
      } else {
        // Return booking details
        return (booking || null) as T;
      }
    }
    
    switch (endpoint) {
      case '/api/dashboard/stats':
        return {
          totalRooms: 120,
          occupiedRooms: 89,
          availableRooms: 31,
          totalGuests: 156,
          todayBookings: 12,
          monthlyRevenue: 125000,
          occupancyRate: 74.2,
          averageRating: 4.6,
          housekeepingTasks: 15,
          maintenanceRequests: 3,
        } as T;
      
      case '/api/rooms':
        return [
          {
            id: 1,
            number: "101",
            type: "Standard",
            floor: 1,
            status: "occupied",
            price: 120,
            capacity: 2,
            amenities: ["WiFi", "TV", "AC"],
            lastCleaned: "2024-01-15T10:00:00Z",
          },
          {
            id: 2,
            number: "102",
            type: "Standard",
            floor: 1,
            status: "available",
            price: 120,
            capacity: 2,
            amenities: ["WiFi", "TV", "AC"],
            lastCleaned: "2024-01-15T14:00:00Z",
          },
          {
            id: 3,
            number: "201",
            type: "Deluxe",
            floor: 2,
            status: "occupied",
            price: 180,
            capacity: 3,
            amenities: ["WiFi", "TV", "AC", "Mini Bar"],
            lastCleaned: "2024-01-15T09:00:00Z",
          },
        ] as T;
      
      case '/api/bookings':
        const bookings = this.data.bookings || [];
        return {
          data: bookings,
          pagination: {
            page: 1,
            limit: 10,
            total: bookings.length,
            total_pages: 1
          }
        } as T;
      
      case '/api/bookings/stats':
        return {
          total_bookings: 156,
          confirmed_bookings: 89,
          canceled_bookings: 12,
          completed_bookings: 45,
          total_revenue: 125000,
          pending_deposits: 15000
        } as T;
      
      case '/api/bookings/config':
        return {
          default_deposit_percentage: 0.3,
          cancellation_policy_hours: 24,
          max_advance_booking_days: 365,
          min_booking_duration_hours: 24
        } as T;
      
      case '/api/bookings/export':
        // Mock export - return a simple CSV blob
        const csvContent = "BookingID,CustomerName,CheckIn,CheckOut,Status,PaymentMethod,TotalAmount,Deposit,Remaining\n501,Ahmed Customer,2025-09-15,2025-09-17,confirmed,mada,1500,450,1050\n502,Sara Client,2025-09-20,2025-09-22,canceled,visa,800,240,560";
        const blob = new Blob([csvContent], { type: 'text/csv' });
        return blob as T;
      
      case '/api/guests':
        return [] as T;
      
      case '/api/housekeeping':
        return [] as T;
      
      case '/api/maintenance':
        return [] as T;
      
      case '/api/staff':
        return [] as T;
      
      case '/api/reports':
        return [] as T;
      
      case '/api/reports/booking':
        return this.generateBookingReport() as T;
      
      case '/api/reports/occupancy':
        return this.generateOccupancyReport() as T;
      
      case '/api/reports/revenue':
        return this.generateRevenueReport() as T;
      
      case '/api/reports/performance':
        return this.generatePerformanceReport() as T;
      
      case '/api/reports/dashboard-summary':
        return this.generateDashboardSummary() as T;
      
      case '/api/drivers':
        return this.data.drivers as T;
      
      case '/api/riders':
        return this.data.riders as T;
      
      case '/api/trips':
        return this.data.trips as T;
      
      case '/api/vehicles':
        return this.data.vehicles as T;
      
      case '/api/complaints':
        return this.data.complaints as T;
      
      case '/api/cities':
        return this.data.cities as T;
      
      case '/api/vehicle-categories':
        return this.data.vehicleCategories as T;
      
      case '/api/finance':
        return this.data.finance as T;
      
      case '/api/notifications':
        return this.data.notifications as T;
      
      case '/api/settings':
        return this.data.settings as T;

      case '/api/pages/admin/all':
        return [
          {
            id: 1,
            slug: 'about-us',
            title_ar: 'من نحن',
            title_en: 'About Us',
            content_ar: '<h2>من نحن</h2><p>نحن مجموعة  رائدة في مجال الشقق المفروشة والإقامة المؤقتة، نقدم خدمات متميزة لعملائنا الكرام.</p><ul><li>خدمة عملاء على مدار الساعة</li><li>شقق عالية الجودة</li><li>أسعار تنافسية</li></ul>',
            content_en: '<h2>About Us</h2><p>We are a leading platform in furnished apartments and temporary accommodation, providing exceptional services to our valued customers.</p><ul><li>24/7 customer service</li><li>High-quality apartments</li><li>Competitive prices</li></ul>',
            isActive: true,
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: '2024-01-15T10:30:00Z',
          },
          {
            id: 2,
            slug: 'privacy-policy',
            title_ar: 'سياسة الخصوصية',
            title_en: 'Privacy Policy',
            content_ar: '<h2>سياسة الخصوصية</h2><p>نحن نحترم خصوصيتك ونلتزم بحماية معلوماتك الشخصية وفقاً لأعلى المعايير الأمنية.</p>',
            content_en: '<h2>Privacy Policy</h2><p>We respect your privacy and are committed to protecting your personal information according to the highest security standards.</p>',
            isActive: true,
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: '2024-01-10T14:20:00Z',
          },
          {
            id: 3,
            slug: 'terms-conditions',
            title_ar: 'الشروط والأحكام',
            title_en: 'Terms & Conditions',
            content_ar: '<h2>الشروط والأحكام</h2><p>يرجى قراءة هذه الشروط والأحكام بعناية قبل استخدام خدماتنا.</p>',
            content_en: '<h2>Terms & Conditions</h2><p>Please read these terms and conditions carefully before using our services.</p>',
            isActive: true,
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: '2024-01-05T09:15:00Z',
          },
          {
            id: 4,
            slug: 'contact',
            title_ar: 'اتصل بنا',
            title_en: 'Contact Us',
            content_ar: '<h2>اتصل بنا</h2><p>نحن هنا لمساعدتك في أي وقت. لا تتردد في التواصل معنا.</p>',
            content_en: '<h2>Contact Us</h2><p>We are here to help you anytime. Feel free to contact us.</p>',
            isActive: true,
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: '2024-01-12T16:45:00Z',
          },
        ] as T;

      case '/api/contact-info':
        return [
          {
            id: 1,
            type: 'phone',
            value: '+966 50 123 4567',
            label_ar: 'رقم الهاتف',
            label_en: 'Phone Number',
            isActive: true,
            order: 1,
          },
          {
            id: 2,
            type: 'email',
            value: 'info@badeel.com',
            label_ar: 'البريد الإلكتروني',
            label_en: 'Email Address',
            isActive: true,
            order: 2,
          },
          {
            id: 3,
            type: 'whatsapp',
            value: '+966 50 123 4567',
            label_ar: 'واتساب',
            label_en: 'WhatsApp',
            isActive: true,
            order: 3,
          },
          {
            id: 4,
            type: 'address',
            value: 'الرياض، المملكة العربية السعودية',
            label_ar: 'العنوان',
            label_en: 'Address',
            isActive: true,
            order: 4,
          },
        ] as T;

      case '/api/gallery':
        return [
          {
            id: 1,
            url: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
            alt_ar: 'شقة فاخرة في الرياض',
            alt_en: 'Luxury apartment in Riyadh',
            caption_ar: 'شقة فاخرة مجهزة بأحدث التقنيات',
            caption_en: 'Luxury apartment equipped with latest technology',
            category: 'apartments',
            isActive: true,
            order: 1,
            createdAt: '2024-01-01T00:00:00Z',
          },
          {
            id: 2,
            url: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800',
            alt_ar: 'غرفة معيشة مريحة',
            alt_en: 'Comfortable living room',
            caption_ar: 'غرفة معيشة واسعة ومريحة',
            caption_en: 'Spacious and comfortable living room',
            category: 'interior',
            isActive: true,
            order: 2,
            createdAt: '2024-01-02T00:00:00Z',
          },
          {
            id: 3,
            url: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800',
            alt_ar: 'مطبخ حديث',
            alt_en: 'Modern kitchen',
            caption_ar: 'مطبخ مجهز بأحدث الأجهزة',
            caption_en: 'Kitchen equipped with modern appliances',
            category: 'interior',
            isActive: true,
            order: 3,
            createdAt: '2024-01-03T00:00:00Z',
          },
          {
            id: 4,
            url: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800',
            alt_ar: 'غرفة نوم أنيقة',
            alt_en: 'Elegant bedroom',
            caption_ar: 'غرفة نوم أنيقة ومريحة',
            caption_en: 'Elegant and comfortable bedroom',
            category: 'interior',
            isActive: true,
            order: 4,
            createdAt: '2024-01-04T00:00:00Z',
          },
        ] as T;
      
      case '/api/hotels':
        if (!(this.data as any).hotels) {
          (this.data as any).hotels = [
            {
              id: 1,
              name: "Grand Plaza Hotel",
              location: "New York, NY",
              rating: 4.8,
              totalRooms: 250,
              availableRooms: 45,
              priceRange: "$150-$400",
              status: "active",
              amenities: ["WiFi", "Pool", "Spa", "Restaurant"],
              lastUpdated: "2024-01-15T10:00:00Z",
            },
            {
              id: 2,
              name: "Hilton Downtown",
              location: "Los Angeles, CA",
              rating: 4.6,
              totalRooms: 180,
              availableRooms: 32,
              priceRange: "$200-$500",
              status: "active",
              amenities: ["WiFi", "Gym", "Bar", "Conference Room"],
              lastUpdated: "2024-01-15T09:30:00Z",
            },
            {
              id: 3,
              name: "Marriott Resort",
              location: "Miami, FL",
              rating: 4.9,
              totalRooms: 320,
              availableRooms: 78,
              priceRange: "$300-$800",
              status: "active",
              amenities: ["WiFi", "Beach Access", "Golf", "Spa"],
              lastUpdated: "2024-01-15T08:45:00Z",
            },
          ];
        }
        return (this.data as any).hotels as T;
      
      case '/api/users':
        return [
          {
            id: 1,
            name: "John Smith",
            email: "john.smith@email.com",
            phone: "+1-555-0123",
            role: "user",
            status: "active",
            joinDate: "2024-01-15T10:00:00Z",
            lastLogin: "2024-01-20T14:30:00Z",
            totalBookings: 12,
            totalSpent: 2400,
            avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
          },
          {
            id: 2,
            name: "Sarah Johnson",
            email: "sarah.j@email.com",
            phone: "+1-555-0124",
            role: "premium",
            status: "active",
            joinDate: "2023-11-20T09:15:00Z",
            lastLogin: "2024-01-19T16:45:00Z",
            totalBookings: 25,
            totalSpent: 5200,
            avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face"
          },
          {
            id: 3,
            name: "Michael Brown",
            email: "mike.brown@email.com",
            phone: "+1-555-0125",
            role: "user",
            status: "inactive",
            joinDate: "2023-08-10T11:20:00Z",
            lastLogin: "2024-01-10T10:15:00Z",
            totalBookings: 8,
            totalSpent: 1600,
            avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
          },
          {
            id: 4,
            name: "Emily Davis",
            email: "emily.davis@email.com",
            phone: "+1-555-0126",
            role: "premium",
            status: "active",
            joinDate: "2023-12-05T14:30:00Z",
            lastLogin: "2024-01-20T12:00:00Z",
            totalBookings: 18,
            totalSpent: 3800,
            avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
          },
          {
            id: 5,
            name: "David Wilson",
            email: "david.wilson@email.com",
            phone: "+1-555-0127",
            role: "user",
            status: "suspended",
            joinDate: "2023-09-15T16:45:00Z",
            lastLogin: "2024-01-15T08:30:00Z",
            totalBookings: 5,
            totalSpent: 1200,
            avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face"
          }
        ] as T;

      case '/api/reviews':
        return [
          {
            id: 1,
            hotelName: "Grand Plaza Hotel",
            userName: "John Smith",
            rating: 5,
            title: "Excellent stay!",
            comment: "Amazing hotel with great service. The room was clean and comfortable. Staff was very friendly and helpful.",
            status: "approved",
            createdAt: "2024-01-18T10:30:00Z",
            helpful: 12,
            notHelpful: 1,
            response: "Thank you for your wonderful review! We're glad you enjoyed your stay."
          },
          {
            id: 2,
            hotelName: "Ocean View Resort",
            userName: "Sarah Johnson",
            rating: 4,
            title: "Great location",
            comment: "Beautiful ocean view and convenient location. Room was spacious and clean. Would recommend!",
            status: "approved",
            createdAt: "2024-01-17T14:20:00Z",
            helpful: 8,
            notHelpful: 2
          },
          {
            id: 3,
            hotelName: "City Center Hotel",
            userName: "Michael Brown",
            rating: 3,
            title: "Average experience",
            comment: "Hotel was okay but could be better. Room was clean but a bit small. Service was decent.",
            status: "pending",
            createdAt: "2024-01-19T09:15:00Z",
            helpful: 3,
            notHelpful: 5
          },
          {
            id: 4,
            hotelName: "Mountain Lodge",
            userName: "Emily Davis",
            rating: 5,
            title: "Perfect mountain getaway",
            comment: "Absolutely stunning views and cozy rooms. The staff went above and beyond. Highly recommend!",
            status: "approved",
            createdAt: "2024-01-16T16:45:00Z",
            helpful: 15,
            notHelpful: 0,
            response: "We're thrilled you enjoyed your mountain retreat! Come back soon!"
          },
          {
            id: 5,
            hotelName: "Business Inn",
            userName: "David Wilson",
            rating: 2,
            title: "Disappointing",
            comment: "Not worth the price. Room was dirty and service was poor. Would not recommend.",
            status: "rejected",
            createdAt: "2024-01-15T11:30:00Z",
            helpful: 2,
            notHelpful: 10
          }
        ] as T;

      case '/api/payments':
        return [
          {
            id: 1,
            bookingId: "BK001234",
            userId: "USR001",
            userName: "John Smith",
            hotelName: "Grand Plaza Hotel",
            amount: 450,
            currency: "USD",
            paymentMethod: "credit_card",
            status: "completed",
            transactionId: "TXN123456789",
            createdAt: "2024-01-18T10:30:00Z",
            processedAt: "2024-01-18T10:32:00Z"
          },
          {
            id: 2,
            bookingId: "BK001235",
            userId: "USR002",
            userName: "Sarah Johnson",
            hotelName: "Ocean View Resort",
            amount: 320,
            currency: "USD",
            paymentMethod: "paypal",
            status: "completed",
            transactionId: "TXN123456790",
            createdAt: "2024-01-17T14:20:00Z",
            processedAt: "2024-01-17T14:22:00Z"
          },
          {
            id: 3,
            bookingId: "BK001236",
            userId: "USR003",
            userName: "Michael Brown",
            hotelName: "City Center Hotel",
            amount: 280,
            currency: "USD",
            paymentMethod: "debit_card",
            status: "pending",
            transactionId: "TXN123456791",
            createdAt: "2024-01-19T09:15:00Z"
          },
          {
            id: 4,
            bookingId: "BK001237",
            userId: "USR004",
            userName: "Emily Davis",
            hotelName: "Mountain Lodge",
            amount: 650,
            currency: "USD",
            paymentMethod: "credit_card",
            status: "completed",
            transactionId: "TXN123456792",
            createdAt: "2024-01-16T16:45:00Z",
            processedAt: "2024-01-16T16:47:00Z"
          },
          {
            id: 5,
            bookingId: "BK001238",
            userId: "USR005",
            userName: "David Wilson",
            hotelName: "Business Inn",
            amount: 200,
            currency: "USD",
            paymentMethod: "bank_transfer",
            status: "failed",
            transactionId: "TXN123456793",
            createdAt: "2024-01-15T11:30:00Z"
          },
          {
            id: 6,
            bookingId: "BK001239",
            userId: "USR001",
            userName: "John Smith",
            hotelName: "Grand Plaza Hotel",
            amount: 450,
            currency: "USD",
            paymentMethod: "credit_card",
            status: "refunded",
            transactionId: "TXN123456794",
            createdAt: "2024-01-10T10:30:00Z",
            processedAt: "2024-01-10T10:32:00Z",
            refundAmount: 450,
            refundReason: "Guest cancelled booking"
          }
        ] as T;


      case '/api/support':
        return [] as T;
      
      // Coupon endpoints
      case '/api/admin/coupons':
        return {
          data: this.data.coupons || [],
          total: (this.data.coupons || []).length
        } as T;
      
      case '/api/admin/coupons/stats':
        return {
          data: this.data.couponStats || {
            totalCoupons: 0,
            activeCoupons: 0,
            expiredCoupons: 0,
            totalUsage: 0,
            totalDiscountGiven: 0,
            averageDiscount: 0,
            topPerformingCoupons: []
          }
        } as T;
      
      case '/api/admin/coupons/analytics':
        return {
          data: this.data.couponAnalytics || {
            usageByDate: [],
            usageByCoupon: [],
            usageByCity: [],
            conversionRate: 0,
            averageOrderValue: 0,
            averageOrderValueWithCoupon: 0
          }
        } as T;
      
      case '/api/admin/coupons/usage':
        return {
          data: this.data.couponUsage || [],
          total: (this.data.couponUsage || []).length
        } as T;
      
      default:
        throw new Error(`Mock API: Endpoint ${endpoint} not found`);
    }
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    await delay(500);
    
    // Handle static content operations
    if (endpoint === '/api/contact-info') {
      const newContact = {
        id: Date.now(),
        ...(data as any),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      return { success: true, data: newContact } as T;
    }

    if (endpoint === '/api/gallery/upload') {
      const newImage = {
        id: Date.now(),
        url: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
        alt_ar: 'صورة جديدة',
        alt_en: 'New image',
        caption_ar: 'وصف الصورة',
        caption_en: 'Image description',
        category: 'general',
        isActive: true,
        order: 1,
        createdAt: new Date().toISOString(),
      };
      return { success: true, data: newImage } as T;
    }

    // Handle booking operations
    if (endpoint.startsWith('/api/bookings/')) {
      if (endpoint.includes('/create')) {
        // Create new booking
        const newBooking = {
          id: Date.now(),
          ...(data as any),
          status: 'confirmed',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        // Add to mock data
        if (!this.data.bookings) {
          this.data.bookings = [];
        }
        this.data.bookings.push(newBooking);
        
        return {
          data: {
            booking: newBooking,
            payment_summary: {
              total_amount: 1000,
              deposit_paid: 300,
              remaining_amount: 700,
              payment_method: (data as any).payment_method || 'mada'
            }
          }
        } as T;
      } else if (endpoint.includes('/cancel')) {
        // Cancel booking
        const bookingId = endpoint.split('/')[3];
        const booking = this.data.bookings?.find((b: any) => b.id === parseInt(bookingId));
        
        if (booking) {
          booking.status = 'canceled';
          booking.updated_at = new Date().toISOString();
        }
        
        return {
          message: `Booking #${bookingId} canceled successfully`,
          refund_amount: booking?.payment_summary?.deposit_paid || 0
        } as T;
      }
    }
    
    switch (endpoint) {
      case '/api/auth/login':
        const credentials = data as { email?: string; phone?: string; password: string };
        // Accept both email and phone for backward compatibility
        const identifier = credentials.email || credentials.phone;
        if ((identifier === 'admin@example.com' || identifier === '+966501234567' || identifier === '+966123456789') && credentials.password === 'password') {
          return {
            success: true,
            data: {
              token: 'mock-jwt-token',
              user: this.data.currentUser
            }
          } as T;
        }
        return {
          success: false,
          message: 'Invalid credentials'
        } as T;
      
      case '/api/notifications':
        const newNotification = {
          id: Date.now(),
          ...(data as Record<string, any>),
          createdAt: new Date().toISOString()
        };
        return newNotification as T;
      
      case '/api/users':
      case '/api/reviews':
      case '/api/payments':
        const newItem = {
          id: Date.now(),
          ...(data as Record<string, any>),
          createdAt: new Date().toISOString()
        };
        return newItem as T;
      
      case '/api/hotels':
        const newHotel = {
          id: Date.now(),
          ...(data as Record<string, any>),
          createdAt: new Date().toISOString(),
        };
        if (!(this.data as any).hotels) (this.data as any).hotels = [];
        (this.data as any).hotels.unshift(newHotel);
        return newHotel as T;
      
      // Coupon POST endpoints
      case '/api/admin/coupons':
        const newCoupon = {
          id: Date.now().toString(),
          ...(data as any),
          totalUsage: 0,
          remainingUsage: (data as any).usageLimit || 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        if (!this.data.coupons) this.data.coupons = [];
        this.data.coupons.unshift(newCoupon);
        return { data: newCoupon } as T;
      
      case '/api/user/validate-coupon':
        // Mock validation response
        return {
          isValid: true,
          discountAmount: 50,
          finalAmount: (data as any).orderAmount - 50,
          coupon: {
            id: "1",
            code: (data as any).code,
            name: "Test Coupon",
            discountType: "fixed",
            discountValue: 50
          }
        } as T;
      
      default:
        throw new Error(`Mock API: POST ${endpoint} not implemented`);
    }
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    await delay(400);
    
    const match = endpoint.match(/\/api\/(\w+)\/(\d+)/);
    if (!match) {
      throw new Error(`Mock API: Invalid PUT endpoint ${endpoint}`);
    }
    
    const [, resource, id] = match;
    const resourceData = this.data[resource as keyof typeof this.data] as any[];
    
    if (!Array.isArray(resourceData)) {
      throw new Error(`Mock API: Resource ${resource} not found`);
    }
    
    const index = resourceData.findIndex(item => item.id === parseInt(id));
    if (index === -1) {
      throw new Error(`Mock API: ${resource} with id ${id} not found`);
    }
    
    resourceData[index] = { ...resourceData[index], ...(data as Record<string, any>) };
    return resourceData[index] as T;
  }

  async delete<T>(endpoint: string): Promise<T> {
    await delay(300);
    
    const match = endpoint.match(/\/api\/(\w+)\/(\d+)/);
    if (!match) {
      throw new Error(`Mock API: Invalid DELETE endpoint ${endpoint}`);
    }
    
    const [, resource, id] = match;
    const resourceData = this.data[resource as keyof typeof this.data] as any[];
    
    if (!Array.isArray(resourceData)) {
      throw new Error(`Mock API: Resource ${resource} not found`);
    }
    
    const index = resourceData.findIndex(item => item.id === parseInt(id));
    if (index === -1) {
      throw new Error(`Mock API: ${resource} with id ${id} not found`);
    }
    
    const deleted = resourceData.splice(index, 1)[0];
    return deleted as T;
  }

  async login(credentials: { email: string; password: string }) {
    return this.post('/api/auth/login', credentials);
  }

  async getCurrentUser() {
    await delay(200);
    return this.data.currentUser;
  }

  // Reports generation methods
  private generateBookingReport() {
    const bookings = this.data.bookings || [];
    const totalBookings = bookings.length;
    const confirmedBookings = bookings.filter((b: any) => b.status === 'confirmed').length;
    const canceledBookings = bookings.filter((b: any) => b.status === 'canceled').length;
    const completedBookings = bookings.filter((b: any) => b.status === 'completed').length;
    const pendingBookings = bookings.filter((b: any) => b.status === 'pending').length;
    
    const totalRevenue = bookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
    const averageBookingValue = totalBookings > 0 ? totalRevenue / totalBookings : 0;
    const cancellationRate = totalBookings > 0 ? (canceledBookings / totalBookings) * 100 : 0;
    const completionRate = totalBookings > 0 ? (completedBookings / totalBookings) * 100 : 0;

    // Generate period data (last 30 days)
    const bookingsByPeriod = this.generatePeriodData(bookings);
    const bookingsByRoomType = this.generateRoomTypeData(bookings);
    const bookingsByPaymentMethod = this.generatePaymentMethodData(bookings);
    const bookingsByStatus = this.generateStatusData(bookings);
    const topCustomers = this.generateTopCustomersData(bookings);
    const bookingTrends = this.generateTrendData(bookings);

    return {
      total_bookings: totalBookings,
      confirmed_bookings: confirmedBookings,
      canceled_bookings: canceledBookings,
      completed_bookings: completedBookings,
      pending_bookings: pendingBookings,
      total_revenue: totalRevenue,
      average_booking_value: averageBookingValue,
      cancellation_rate: cancellationRate,
      completion_rate: completionRate,
      bookings_by_period: bookingsByPeriod,
      bookings_by_room_type: bookingsByRoomType,
      bookings_by_payment_method: bookingsByPaymentMethod,
      bookings_by_status: bookingsByStatus,
      top_customers: topCustomers,
      booking_trends: bookingTrends
    };
  }

  private generateOccupancyReport() {
    const bookings = this.data.bookings || [];
    const totalRooms = 120; // Mock total rooms
    const occupiedRooms = bookings.filter((b: any) => 
      b.status === 'confirmed' || b.status === 'completed'
    ).length;
    const availableRooms = totalRooms - occupiedRooms;
    const occupancyRate = totalRooms > 0 ? (occupiedRooms / totalRooms) * 100 : 0;
    
    const totalRevenue = bookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
    const averageDailyRate = occupiedRooms > 0 ? totalRevenue / occupiedRooms : 0;
    const revenuePerAvailableRoom = totalRooms > 0 ? totalRevenue / totalRooms : 0;

    return {
      total_rooms: totalRooms,
      occupied_rooms: occupiedRooms,
      available_rooms: availableRooms,
      occupancy_rate: occupancyRate,
      average_daily_rate: averageDailyRate,
      revenue_per_available_room: revenuePerAvailableRoom,
      occupancy_by_room_type: this.generateOccupancyByRoomType(bookings),
      occupancy_trends: this.generateOccupancyTrends(bookings),
      seasonal_analysis: this.generateSeasonalAnalysis(bookings)
    };
  }

  private generateRevenueReport() {
    const bookings = this.data.bookings || [];
    const totalRevenue = bookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
    const roomRevenue = totalRevenue * 0.85; // 85% from rooms
    const serviceRevenue = totalRevenue * 0.10; // 10% from services
    const taxRevenue = totalRevenue * 0.05; // 5% from taxes
    const refunds = bookings.filter((b: any) => b.status === 'canceled')
      .reduce((sum: number, b: any) => sum + (b.payment_summary?.deposit_paid || 0), 0);
    const netRevenue = totalRevenue - refunds;

    return {
      total_revenue: totalRevenue,
      room_revenue: roomRevenue,
      service_revenue: serviceRevenue,
      tax_revenue: taxRevenue,
      refunds: refunds,
      net_revenue: netRevenue,
      revenue_by_period: this.generateRevenuePeriodData(bookings),
      revenue_by_room_type: this.generateRevenueRoomTypeData(bookings),
      revenue_by_payment_method: this.generateRevenuePaymentMethodData(bookings),
      revenue_trends: this.generateRevenueTrends(bookings),
      profit_margin_analysis: this.generateProfitMarginData(bookings)
    };
  }

  private generatePerformanceReport() {
    return {
      key_performance_indicators: [
        { metric: 'Occupancy Rate', value: 74.2, target: 80, unit: '%', trend: 'up', change_percentage: 5.2 },
        { metric: 'Average Daily Rate', value: 156, target: 160, unit: 'SAR', trend: 'down', change_percentage: -2.1 },
        { metric: 'Revenue per Available Room', value: 115, target: 120, unit: 'SAR', trend: 'up', change_percentage: 3.8 },
        { metric: 'Customer Satisfaction', value: 4.6, target: 4.5, unit: '/5', trend: 'up', change_percentage: 2.2 },
        { metric: 'Booking Conversion Rate', value: 68, target: 70, unit: '%', trend: 'down', change_percentage: -1.5 }
      ],
      customer_satisfaction: {
        average_rating: 4.6,
        total_reviews: 234,
        rating_distribution: [
          { rating: 5, count: 145, percentage: 62 },
          { rating: 4, count: 67, percentage: 29 },
          { rating: 3, count: 15, percentage: 6 },
          { rating: 2, count: 5, percentage: 2 },
          { rating: 1, count: 2, percentage: 1 }
        ],
        review_trends: this.generateReviewTrends()
      },
      operational_efficiency: {
        average_check_in_time: 8.5,
        average_check_out_time: 6.2,
        room_turnover_rate: 2.3,
        maintenance_response_time: 2.1,
        staff_productivity_score: 87
      },
      financial_health: {
        cash_flow: 45000,
        accounts_receivable: 12000,
        accounts_payable: 8500,
        profit_margin: 23.5,
        return_on_investment: 18.2,
        debt_to_equity_ratio: 0.35
      }
    };
  }

  private generateDashboardSummary() {
    const bookings = this.data.bookings || [];
    const todayBookings = bookings.filter((b: any) => {
      const today = new Date().toISOString().split('T')[0];
      return b.created_at?.startsWith(today);
    }).length;
    
    const todayRevenue = bookings.filter((b: any) => {
      const today = new Date().toISOString().split('T')[0];
      return b.created_at?.startsWith(today);
    }).reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);

    return {
      today_summary: {
        bookings: todayBookings,
        revenue: todayRevenue,
        occupancy_rate: 74.2,
        average_rating: 4.6
      },
      period_comparison: {
        bookings_change: 12.5,
        revenue_change: 8.3,
        occupancy_change: 5.2,
        rating_change: 2.1
      },
      top_metrics: {
        best_performing_room_type: 'Deluxe Suite',
        most_popular_payment_method: 'Mada',
        peak_booking_day: 'Friday',
        highest_revenue_period: 'December 2024'
      },
      alerts: {
        low_occupancy: false,
        high_cancellation_rate: false,
        payment_issues: false,
        maintenance_required: true
      }
    };
  }

  // Helper methods for generating specific data
  private generatePeriodData(bookings: any[]) {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last30Days.map(date => {
      const dayBookings = bookings.filter((b: any) => b.created_at?.startsWith(date));
      const revenue = dayBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      return {
        period: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        date,
        bookings_count: dayBookings.length,
        revenue,
        occupancy_rate: Math.random() * 100
      };
    });
  }

  private generateRoomTypeData(bookings: any[]) {
    const roomTypes = ['Standard', 'Deluxe', 'Suite', 'Presidential'];
    return roomTypes.map(type => {
      const typeBookings = bookings.filter((b: any) => b.room_type === type);
      const revenue = typeBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      const totalNights = typeBookings.length * 2; // Mock average 2 nights
      return {
        room_type: type,
        bookings_count: typeBookings.length,
        revenue,
        average_nightly_rate: typeBookings.length > 0 ? revenue / totalNights : 0,
        occupancy_rate: Math.random() * 100,
        total_nights: totalNights
      };
    });
  }

  private generatePaymentMethodData(bookings: any[]) {
    const methods = ['mada', 'visa', 'mastercard', 'cash', 'bank_transfer'];
    const totalBookings = bookings.length;
    
    return methods.map(method => {
      const methodBookings = bookings.filter((b: any) => b.payment_summary?.payment_method === method);
      const revenue = methodBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      return {
        payment_method: method,
        bookings_count: methodBookings.length,
        revenue,
        percentage: totalBookings > 0 ? (methodBookings.length / totalBookings) * 100 : 0,
        average_transaction_value: methodBookings.length > 0 ? revenue / methodBookings.length : 0
      };
    });
  }

  private generateStatusData(bookings: any[]) {
    const statuses = ['confirmed', 'canceled', 'completed', 'pending'];
    const totalBookings = bookings.length;
    
    return statuses.map(status => {
      const statusBookings = bookings.filter((b: any) => b.status === status);
      const revenue = statusBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      return {
        status,
        count: statusBookings.length,
        percentage: totalBookings > 0 ? (statusBookings.length / totalBookings) * 100 : 0,
        revenue
      };
    });
  }

  private generateTopCustomersData(bookings: any[]) {
    const customerMap = new Map();
    
    bookings.forEach((booking: any) => {
      const customerId = booking.customer?.id;
      if (customerId) {
        if (!customerMap.has(customerId)) {
          customerMap.set(customerId, {
            customer_id: customerId,
            customer_name: booking.customer?.name || 'Unknown',
            email: booking.customer?.email || '',
            total_bookings: 0,
            total_spent: 0,
            last_booking_date: booking.created_at,
            average_booking_value: 0
          });
        }
        
        const customer = customerMap.get(customerId);
        customer.total_bookings++;
        customer.total_spent += booking.payment_summary?.total_amount || 0;
        customer.average_booking_value = customer.total_spent / customer.total_bookings;
        
        if (new Date(booking.created_at) > new Date(customer.last_booking_date)) {
          customer.last_booking_date = booking.created_at;
        }
      }
    });
    
    return Array.from(customerMap.values())
      .sort((a, b) => b.total_spent - a.total_spent)
      .slice(0, 10);
  }

  private generateTrendData(bookings: any[]) {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last7Days.map(date => {
      const dayBookings = bookings.filter((b: any) => b.created_at?.startsWith(date));
      const revenue = dayBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      return {
        date,
        bookings: dayBookings.length,
        revenue,
        occupancy_rate: Math.random() * 100
      };
    });
  }

  private generateOccupancyByRoomType(bookings: any[]) {
    const roomTypes = ['Standard', 'Deluxe', 'Suite', 'Presidential'];
    return roomTypes.map(type => {
      const typeBookings = bookings.filter((b: any) => b.room_type === type);
      const totalRooms = 30; // Mock 30 rooms per type
      const occupiedRooms = typeBookings.length;
      const revenue = typeBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      
      return {
        room_type: type,
        total_rooms: totalRooms,
        occupied_rooms: occupiedRooms,
        occupancy_rate: totalRooms > 0 ? (occupiedRooms / totalRooms) * 100 : 0,
        average_daily_rate: occupiedRooms > 0 ? revenue / occupiedRooms : 0,
        revenue
      };
    });
  }

  private generateOccupancyTrends(bookings: any[]) {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last30Days.map(date => {
      const dayBookings = bookings.filter((b: any) => b.created_at?.startsWith(date));
      const totalRooms = 120;
      const occupiedRooms = dayBookings.length;
      const revenue = dayBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      
      return {
        date,
        occupancy_rate: totalRooms > 0 ? (occupiedRooms / totalRooms) * 100 : 0,
        average_daily_rate: occupiedRooms > 0 ? revenue / occupiedRooms : 0,
        revenue_per_available_room: totalRooms > 0 ? revenue / totalRooms : 0,
        total_rooms: totalRooms,
        occupied_rooms: occupiedRooms
      };
    });
  }

  private generateSeasonalAnalysis(bookings: any[]) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map(month => ({
      month,
      occupancy_rate: Math.random() * 100,
      average_daily_rate: 120 + Math.random() * 80,
      revenue: Math.random() * 50000,
      bookings_count: Math.floor(Math.random() * 100)
    }));
  }

  private generateRevenuePeriodData(bookings: any[]) {
    const last12Months = Array.from({ length: 12 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      return date.toISOString().substring(0, 7); // YYYY-MM format
    }).reverse();

    return last12Months.map(period => {
      const monthBookings = bookings.filter((b: any) => b.created_at?.startsWith(period));
      const revenue = monthBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      return {
        period: new Date(period + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        date: period,
        revenue,
        bookings_count: monthBookings.length,
        average_booking_value: monthBookings.length > 0 ? revenue / monthBookings.length : 0,
        growth_rate: Math.random() * 20 - 10 // Random growth rate between -10% and 10%
      };
    });
  }

  private generateRevenueRoomTypeData(bookings: any[]) {
    const roomTypes = ['Standard', 'Deluxe', 'Suite', 'Presidential'];
    const totalRevenue = bookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
    
    return roomTypes.map(type => {
      const typeBookings = bookings.filter((b: any) => b.room_type === type);
      const revenue = typeBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      return {
        room_type: type,
        revenue,
        bookings_count: typeBookings.length,
        average_booking_value: typeBookings.length > 0 ? revenue / typeBookings.length : 0,
        percentage: totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0
      };
    });
  }

  private generateRevenuePaymentMethodData(bookings: any[]) {
    const methods = ['mada', 'visa', 'mastercard', 'cash', 'bank_transfer'];
    const totalRevenue = bookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
    
    return methods.map(method => {
      const methodBookings = bookings.filter((b: any) => b.payment_summary?.payment_method === method);
      const revenue = methodBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      return {
        payment_method: method,
        revenue,
        transactions_count: methodBookings.length,
        average_transaction_value: methodBookings.length > 0 ? revenue / methodBookings.length : 0,
        percentage: totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0
      };
    });
  }

  private generateRevenueTrends(bookings: any[]) {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last30Days.map(date => {
      const dayBookings = bookings.filter((b: any) => b.created_at?.startsWith(date));
      const revenue = dayBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      return {
        date,
        revenue,
        bookings: dayBookings.length,
        average_booking_value: dayBookings.length > 0 ? revenue / dayBookings.length : 0,
        growth_rate: Math.random() * 20 - 10
      };
    });
  }

  private generateProfitMarginData(bookings: any[]) {
    const last12Months = Array.from({ length: 12 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      return date.toISOString().substring(0, 7);
    }).reverse();

    return last12Months.map(period => {
      const monthBookings = bookings.filter((b: any) => b.created_at?.startsWith(period));
      const revenue = monthBookings.reduce((sum: number, b: any) => sum + (b.payment_summary?.total_amount || 0), 0);
      const costs = revenue * 0.65; // Mock 65% costs
      const profit = revenue - costs;
      return {
        period: new Date(period + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        revenue,
        costs,
        profit,
        margin_percentage: revenue > 0 ? (profit / revenue) * 100 : 0
      };
    });
  }

  private generateReviewTrends() {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last30Days.map(date => ({
      date,
      average_rating: 4.2 + Math.random() * 0.8,
      reviews_count: Math.floor(Math.random() * 10)
    }));
  }
}

export const mockApi = new MockApi();